/** ***********
*
* <dialog-edit-group.component.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*  NG6C740 [thomas.pereira.external@airbus.com]
*
* All rights reserved.
**************/

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { isEqual, pick, unionBy, uniqBy } from 'lodash';
import { Observable, Subscription, combineLatest, firstValueFrom, of } from 'rxjs';
import { User } from 'src/app/models/user';
import { Group, GroupFamily } from 'src/app/models/user-right';
import {
    selectAllUsers$,
    selectFamiliesOfGroup$,
    selectFamiliesOfGroupLoading$,
    selectGroupById$,
    selectMyRightsTree$,
    selectUserLoaded$,
    selectUserRightLoading$,
    selectUsersOfGroup$,
    selectUsersOfGroupLoading$,
} from 'src/app/store/selectors/user-right.selectors';
import * as featureActions from 'src/app/store/actions/user-right.action';
import { EffectResult, UserRightEffects } from 'src/app/store/effects/user-right.effects';
import { filter, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { GroupFamilyListItem } from '../_shared/group-families-list/group-families-list.component';
import { SnackbarComponent } from 'src/app/components/shared/snackbar/snackbar';
import { MatExpansionPanel } from '@angular/material/expansion';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SelectUserComponent, SelectUserDialogData } from '../_shared/select-user/select-user.component';
import { DialogValidationComponent, ValidationDialogData } from 'src/app/components/shared/dialog/dialog-validation/dialog-validation.component';
import { GroupCreate, GroupUpdate, UserRightService } from 'src/app/services/user-right.service';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { selectProfiles$, selectProfileUserRights$ } from 'src/app/store/selectors/profile.selectors';
import { MatChipInputEvent } from '@angular/material/chips';

export interface GroupDialogData {
  group?: Group;
  action: 'create'|'update';
}

const MAX_MANAGERS = 2;

@Component({
    selector: 'app-dialog-edit-group',
    templateUrl: './dialog-edit-group.component.html',
    styleUrls: ['./dialog-edit-group.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DialogEditGroupComponent implements OnInit, OnDestroy {

  public title = '';
  public groupForm: FormGroup<{
    name: FormControl<string>;
    description: FormControl<string>;
    managers: FormControl<User['id'][]>;
    mobile: FormControl<boolean>;
  }> = this._fb.group({
    name: this._fb.control('', Validators.required),
    description: this._fb.control(''),
    managers: this._fb.control([], Validators.maxLength(MAX_MANAGERS)),
    mobile: this._fb.control(false),
  });
  public group$?: Observable<Group>;
  public owner$: Observable<User>;
  public users: (User & { removed?: boolean })[] = [];
  public displayedManagers: User[] = [];
  public readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  public usersLoading = false;
  public maintainUsers = false;
  public families: (GroupFamilyListItem & { removed?: boolean })[] = [];
  public familiesLoading = false;
  public maintainFamilies = false;
  public editInProgress = false;
  public usersLoading$ = this.store.select(selectUserLoaded$).pipe(
    map(v => !v),
  );
  public isAdmin$ = this.store.select(selectProfileUserRights$).pipe(
    map(rights => rights.isAdmin)
  );

  private _subscription = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: GroupDialogData,
    private _dialogRef: MatDialogRef<DialogEditGroupComponent>,
    private _dialog: MatDialog,
    private store: Store,
    private _fb: FormBuilder,
    private _cd: ChangeDetectorRef,
    private _translate: TranslateService,
    private _userRightEffects: UserRightEffects,
    private _snackBar: SnackbarComponent,
    private _userRightService: UserRightService,
  ) { }

  ngOnInit(): void {
    this._subscription.add(
      this.groupForm.valueChanges.subscribe(
        () => this._cd.markForCheck()
      )
    );
    this._subscription.add(
        this.groupForm.controls.managers.valueChanges.pipe(
            switchMap((selection) => combineLatest([
                this.store.select(selectAllUsers$),
                this.usersLoading$,
                this._translate.onLangChange.pipe(
                    startWith(this._translate.currentLang),
                ),
            ]).pipe(
                map(([users, loading]) => {
                    if (loading) {
                        return selection.map(
                            (id) => ({ id, userId: this._translate.instant('LOADING_DATA') })
                        );
                    }
                    return users.filter(
                        ({ id }) => selection.includes(id)
                    );
                }),
            )),
        ).subscribe(
            (users: User[]) => {
                this.displayedManagers = unionBy(users, this.displayedManagers, (item) => item.id);
                this._cd.markForCheck();
            }
        )
    );
    this.title = this.data.action === 'update' ?
      this._translate.instant('MANAGE_GROUPS_EDIT_WITH_NAME', { group: this.data.group?.name }) :
      this._translate.instant('MANAGE_GROUPS_CREATE');
    this.groupForm.patchValue({
      name: this.data.group?.name,
      description: this.data.group?.description,
      managers: this.data.group?.managers ?? [],
      mobile: this.data.group?.mobile,
    });
    if (this.data.group) {
        this.group$ = this.store.select(selectGroupById$(this.data.group.id));
        this._subscription.add(
            this.group$.subscribe(
                (group) => this._handleGroupChange(group)
            )
        );
    }
    this._handleMobileFormControlState();

    /**
     * 1. Listen to changes of the manager in the form
     * 2. Retrieve list of users from the store
     * 3. Update value of the list of chips displaying the managers
     * This is done with RXJS only !
     */

    // Preload right tree
    this.store.dispatch(new featureActions.FetchMyRightsTree());
    this._userRightService.checkUserLoaded();
  }

    private _handleGroupChange(group: Group) {
        this.data.group = group;
        if (this.data.action === 'update' && !group?.isOwner) {
            this.groupForm.controls.name.disable();
            this.groupForm.controls.description.disable();
            this.groupForm.controls.managers.disable();
            this.owner$ = this.store.select(selectAllUsers$).pipe(
                map(users => users.find(
                    ({ id }) => id === this.data.group.owner
                )),
                tap(() => this._cd.markForCheck()),
            );
        }
        this._cd.markForCheck();
    }

    /**
     * Handle the state of the mobile form control.
     * The form is only available to users who are owners of the group,
     * and have the mobile specific role
     */
    private _handleMobileFormControlState() {
        this._subscription.add(
            combineLatest([
                this.isAdmin$,
                this.group$ ?? of(null),
            ]).subscribe(
                ([isAdmin, group]) => {
                    const { mobile } = this.groupForm.controls;
                    if (!isAdmin || (this.data.action === 'update' && !group?.isOwner)) {
                        mobile.disable();
                    } else {
                        mobile.enable();
                    }
                }
            )
        );
    }

  /**
   * Close the dialog
   */
  public onClickCancel(): void {
    this._dialogRef.close();
  }

  /**
   * Update an existing group
   */
  private _updateGroup(): Observable<EffectResult['result']> {
    const { value } = this.groupForm;
    const groupData: GroupUpdate = {
        id: this.data.group.id,
        name: value.name,
        description: value.description,
        managers: value.managers,
        mobile: value.mobile,
    };
    // Return if there is no modification to apply
    if (!this.maintainUsers && !this.maintainFamilies && isEqual(
        pick(this.data.group, Object.keys(groupData)),
        groupData,
    )) {
      return;
    }
    const activeUsers = this.users.filter(user => !user.removed).map(user => user.id);
    const activeFamilies = this.families.filter(family => !family.removed).map(
        ({ id, level, scope}) => ({id, level, scope})
    );
    this.store.dispatch(new featureActions.UpdateGroup({
      group: groupData,
      users: this.maintainUsers ? activeUsers : undefined,
      families: this.maintainFamilies ? activeFamilies : undefined,
    }));
    return this._userRightEffects.effectSubject.pipe(
      filter(({ action }) => action === featureActions.ActionTypes.UPDATE_GROUP),
      take(1),
      map(({ result }) => result),
    );
  }

  /**
   * Create a new group
   */
  private _createGroup(): Observable<EffectResult['result']> {
    const { value } = this.groupForm;
    const groupData: GroupCreate = {
        name: value.name,
        description: value.description,
        managers: value.managers,
        mobile: value.mobile,
    };
    this.store.dispatch(new featureActions.CreateGroup({
      group: groupData,
      users: this.maintainUsers ? this.users.map(({ id }) => id) : undefined,
      families: this.maintainFamilies ? this.families.map(
        ({ id, level, scope }) => ({ id, level, scope })
      ) : undefined,
    }));
    return this._userRightEffects.effectSubject.pipe(
      filter(({ action }) => action === featureActions.ActionTypes.CREATE_GROUP),
      take(1),
      map(({ result }) => result),
    );
  }

    public transfertOwnership() {
        this._selectGroupResponsible$({
            multiple: false,
        }).subscribe(
            ([user]: User[]) => {
                const confirmation = this._dialog.open<DialogValidationComponent, ValidationDialogData>(DialogValidationComponent, {
                    data: {
                        textContent: this._translate.instant('GROUP_CONFIRM_TRANSFER_TO', {
                            user: user.userId,
                        }),
                        title: this._translate.instant('GROUP_TRANSFER_OWNERSHIP'),
                        cancelText: this._translate.instant('CANCEL'),
                        validateText: this._translate.instant('CONFIRM'),
                        dangerousAction: true,
                    }
                });
                confirmation.componentInstance.onValidateEvent.pipe(
                    take(1),
                    takeUntil(confirmation.afterClosed()),
                ).subscribe(
                    () => {
                        this.store.dispatch(
                            featureActions.transferGroupOwnership({
                                group: this.data.group,
                                transferedTo: user.id,
                            })
                        );
                        this._subscription.add(
                            this._userRightEffects.effectSubject.pipe(
                                filter(({ action }) => [
                                    featureActions.ActionTypes.TRANSFER_GROUP_OWNERSHIP,
                                    featureActions.ActionTypes.SUCCESS_TRANSFER_GROUP_OWNERSHIP,
                                    featureActions.ActionTypes.ERROR_TRANSFER_GROUP_OWNERSHIP,
                                ].includes(action)),
                                take(1),
                            ).subscribe(
                                ({ result }) => {
                                    if (result === 'success') {
                                        this._snackBar.open(
                                            this._translate.instant('GROUP_OWNERSHIP_TRANSFER_SUCCESS'),
                                            'green-snackbar',
                                        );
                                    }
                                    confirmation.close();
                                }
                            )
                        )
                    }
                )
            }
        );
    }

    public async addManagers(event?: Event) {
        event?.stopImmediatePropagation();
        if (this.groupForm.value.managers.length >= MAX_MANAGERS) {
            this._snackBar.open(
                this._translate.instant("GROUP_CANNOT_ADD_MORE_MANAGER"),
                "orange-snackbar",
            );
            return;
        }
        const self = (await firstValueFrom(this.store.select(selectProfiles$)))?.profiles?.userId;
        const exclude = [...new Set(this.data.group ? [
            this.data.group.owner,
            self,
            ...this.groupForm.value.managers,
        ] : [
            self,
            ...this.groupForm.value.managers,
        ])];
        this._selectGroupResponsible$({
            limit: 2 - this.groupForm.value.managers.length,
            exclude,
        })?.subscribe(
            (users: User[]) => {
                this.groupForm.patchValue({
                    managers: (this.groupForm.value.managers || []).concat(users.map(user => user.id)),
                });
                this.groupForm.controls.managers.markAsDirty();
            }
        )
        this._cd.markForCheck();
    }

    public addManagerFromInput(event: MatChipInputEvent) {
        if (!event.value) {
            return;
        }
        if (this.groupForm.value.managers.length >= MAX_MANAGERS) {
            this._snackBar.open(
                this._translate.instant("GROUP_CANNOT_ADD_MORE_MANAGER"),
                "orange-snackbar",
            );
            return;
        }
        combineLatest([
            this.store.select(selectAllUsers$),
            this.usersLoading$,
        ]).pipe(
            filter(([, loading]) => !loading),
            take(1),
        ).subscribe(
            ([users]) => {
                const foundUser = users.find(
                    ({ email, userId }) => event.value === email || event.value === userId
                );
                if (foundUser) {
                    this.groupForm.patchValue({
                        managers: (this.groupForm.value.managers || []).concat(foundUser.id),
                    });
                    event.chipInput.clear();
                } else {
                    this._snackBar.open(
                        this._translate.instant("USER_NOT_FOUND", { user: event.value }),
                        "orange-snackbar",
                    );
                }
            }
        );
    }

    private _selectGroupResponsible$(params?: SelectUserDialogData): Observable<User[]> {
        if (this.data.action === 'update' && !this.data.group?.isOwner) {
            return;
        }
        const usersDialog = this._dialog.open<SelectUserComponent, SelectUserDialogData, User[]>(SelectUserComponent, {
            width: '400px',
            maxWidth: '90vw',
            minWidth: '25vw',
            data: {
                exclude: [
                    this.data.group?.owner,
                ],
                specificRoles: [
                    'administrator',
                    'adminGroup',
                ],
                displayField: 'email',
               ...params,
            },
        });
        return usersDialog.afterClosed().pipe(
            take(1),
            filter(value => !!value),
        );
    }

  /**
   * Save the modifications done in the form
   */
  public onFormSubmit(): void {
    let editResult$: Observable<EffectResult['result']>;
    switch (this.data.action) {
      case 'update':
        editResult$ = this._updateGroup();
        break;
      case 'create':
        editResult$ = this._createGroup();
        break;
      default:
        throw new RangeError(`The action ${this.data.action} is not in ['update', 'create']`);
    }
    if (editResult$) {
      this.editInProgress = true;
      this._subscription.add(
        editResult$.subscribe(
          (result) => {
            this.editInProgress = false;
                if (result === 'success') {
                    let text: string;
                    switch (this.data.action) {
                        case 'create':
                            text = this._translate.instant('BANNER_SUCCESS_CREATE', { item: this.groupForm.value.name });
                            break;
                        case 'update':
                            text = this._translate.instant('BANNER_SUCCESS_EDIT', { value: this.groupForm.value.name });
                            break;
                    }
                    this._snackBar.open(text, 'green-snackbar', 5000);
                    this._dialogRef.close();
                }
            this._cd.markForCheck();
          }
        )
      );
    } else {
      this._dialogRef.close();
    }
  }

  /**
   * Fetch the users of the group
   */
  public fetchUsers(): void {
    if (this.data.group) {
      this._subscription.add(
        this.store.select(selectUsersOfGroupLoading$).subscribe(
          (loading) => {
            this.usersLoading = loading;
            this._cd.markForCheck();
          }
        )
      );
        this._subscription.add(
            this.store.select(selectUsersOfGroup$(this.data.group)).subscribe(
                (users) => {
                    this.users = uniqBy(this.users.concat(users ?? []), 'id');
                    this._cd.markForCheck();
                }
            )
        );
      this.store.dispatch(new featureActions.FetchUsersOfGroup({ group: this.data.group }));
    } else {
      this._cd.markForCheck();
    }
  }

  /**
   * Add users to the list of users
   * @param users The selected users to add
   */
  public addUsers(users: User[]): void {
    users.forEach(addedUser => {
        const existingUser = this.users.find(u => u === addedUser);
        if (existingUser) {
            existingUser.removed = false;
        } else {
            this.users = [...this.users, addedUser];
        }
    });
    this.groupForm.markAsDirty();
    this._cd.markForCheck();
  }

  /**
   * Remove a user from the list of users
   * @param user The user to remove
   */
    public removeUsers(users: User[]): void {
        users.forEach(removedUser => {
            this.users = this.users.map(user => user === removedUser ? { ...user, removed: true } : user);
        });
        this._cd.markForCheck();
        this.groupForm.markAsDirty();
    }

  /**
     * Remove chip value from chip list
     * @param managerId Chip element related
     */
    public removeManager(managerId: string, event?: Event): void {
        event?.stopImmediatePropagation();
        this.displayedManagers = this.displayedManagers.filter(
            user => user.id !== managerId
        );
        this.groupForm.patchValue(
            {managers: this.displayedManagers.map(manager => manager.id)}
        );
        this.groupForm.markAsDirty();
    }

  /**
   * Fetch the users of the group
   */
 public fetchFamilies(): void {
    if (!this.data.group) {
        this._cd.markForCheck();
        return;
    }

    this._subscription.add(
        combineLatest([
            this.store.select(selectFamiliesOfGroupLoading$),
            this.store.select(selectUserRightLoading$),
        ]).subscribe(
            ([loadingGroup, loadingRightTree]) => {
                this.familiesLoading =
                    loadingGroup || loadingRightTree;

                this._cd.markForCheck();
            }
        )
    );

    this._subscription.add(
        combineLatest([
            this.store.select(
                selectFamiliesOfGroup$(this.data.group)
            ).pipe(
                filter((value) => Array.isArray(value)),
            ),
            this.store.select(selectMyRightsTree$),
        ]).subscribe(
            ([familiesOfGroup, myRights]) => {
                const familiesList = (familiesOfGroup ?? [])
                    .map((item): GroupFamilyListItem => {
                        const parentFamily = myRights.find(
                            ({ families }) =>
                                families.some(
                                    ({ id }) => id === item.id
                                )
                        );

                        return {
                            ...item,
                            parentFamilyName: parentFamily?.name,
                        };
                    })
                    .filter(
                        (family) =>
                            typeof family.parentFamilyName ===
                                'string' &&
                            family.parentFamilyName.trim().length > 0
                    );

                // Latest store/API result is the source of truth.
                this.families = [...familiesList];

                this._cd.markForCheck();
            }
        )
    );

    this.store.dispatch(
        new featureActions.FetchFamiliesOfGroup({
            group: this.data.group,
        })
    );
}

  /**
   * Add families to the list of families
   * @param families The selected families to add
   */
  public addFamilies(families: GroupFamily[]): void {
    this.store.select(selectMyRightsTree$).pipe(
        take(1),
    ).subscribe(
        rightTree => {
            this.families = this.families.concat(families.map(
                fam => ({
                    ...fam,
                    parentFamilyName: rightTree.find(
                        (pf) => pf.families.find(
                            ({ id }) => id === fam.id
                        )
                    )?.name,
                })
            ));
            this.groupForm.markAsDirty();
        }
    );
  }

  /**
   * Set the role level of the family
   * @param family The updated family
   */
  public updateFamilyRole(family: GroupFamily): void {
    const updatedFamily = this.families.find(
      ({ id }) => family.id === id
    );
    updatedFamily.level = family.level;
    this.groupForm.markAsDirty();
  }

  /**
   * Remove families from the list of families
   * @param families The families to remove
   */
    public removeFamilies(families: GroupFamily['id'][]): void {
        this.families = this.families.map( family =>
            families.includes(family.id) ? { ...family, removed: true}: family
        );
        this._cd.markForCheck();
        this.groupForm.markAsDirty();
    }

  public onPageChange(source: MatExpansionPanel) {
    const el = document.querySelector(`#${source.id}`);
    setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth' });
        el.scrollTo(0, 0);
    });
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

}
